import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useRef, useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, Expand } from 'lucide-react';
import { RevealImage, preloadImage } from './RevealImage';

export default function ExperienceSection({ media }) {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-80px" });
  const [activeIndex, setActiveIndex] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const [lightbox, setLightbox] = useState(false);
  const [direction, setDirection] = useState(1);

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('/api')) return `${process.env.REACT_APP_BACKEND_URL}${url}`;
    return url;
  };

  // Pick the best available image URL for display
  const getOptimizedUrl = (item) => resolveUrl(item?.optimized_url || item?.url);
  const getThumbUrl = (item) => resolveUrl(item?.thumb_url || item?.url);

  const items = media || [];
  const activeItem = items[activeIndex];

  // Preload optimized gallery images on mount for instant transitions
  useEffect(() => {
    items.forEach(item => {
      if (item && item.media_type !== 'video') preloadImage(getOptimizedUrl(item));
    });
  }, [items]);

  const go = useCallback((dir) => {
    setDirection(dir);
    setActiveIndex((prev) => {
      const next = prev + dir;
      if (next < 0) return items.length - 1;
      if (next >= items.length) return 0;
      return next;
    });
    setIsAutoPlay(false);
  }, [items.length]);

  // Auto-play
  useEffect(() => {
    if (!isAutoPlay || items.length <= 1) return;
    const timer = setInterval(() => {
      setDirection(1);
      setActiveIndex((prev) => (prev + 1) % items.length);
    }, 4500);
    return () => clearInterval(timer);
  }, [isAutoPlay, items.length]);

  // Keyboard nav
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowLeft') go(-1);
      if (e.key === 'ArrowRight') go(1);
      if (e.key === 'Escape') setLightbox(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [go]);

  if (items.length === 0) return null;

  const slideVariants = {
    enter: (d) => ({ x: d > 0 ? 80 : -80, opacity: 0, scale: 0.95 }),
    center: { x: 0, opacity: 1, scale: 1 },
    exit: (d) => ({ x: d > 0 ? -80 : 80, opacity: 0, scale: 0.95 }),
  };

  return (
    <section id="experience" className="pt-10 md:pt-14 pb-24 md:pb-36 px-6 md:px-12 lg:px-24" data-testid="experience-section">
      <div className="max-w-7xl mx-auto" ref={sectionRef}>

        {/* Section heading */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="mb-14 md:mb-20"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-[1px] bg-gold" />
            <span className="text-gold text-[10px] tracking-[0.4em] uppercase font-body font-medium">
              Highlights
            </span>
          </div>
          <h2 className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase leading-[0.95]">
            The OZFEST<br />
            <span className="text-gold">Experience</span>
          </h2>
        </motion.div>

        {/* Main gallery */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Spotlight viewer */}
          <div className="relative group mb-4">
            {/* Gold ambient glow behind the image */}
            <div className="absolute -inset-3 md:-inset-5 rounded-sm opacity-40 blur-2xl pointer-events-none transition-opacity duration-1000"
              style={{ background: 'radial-gradient(ellipse 70% 60% at 50% 50%, rgba(191,155,48,0.12) 0%, transparent 70%)' }}
            />

            {/* Main image container */}
            <div
              className="relative aspect-[16/9] md:aspect-[2.35/1] overflow-hidden bg-surface cursor-pointer"
              onClick={() => setLightbox(true)}
              data-testid="gallery-main"
            >
              <AnimatePresence initial={false} custom={direction} mode="popLayout">
                <motion.div
                  key={activeIndex}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
                  className="absolute inset-0"
                >
                  {activeItem?.media_type === 'video' && activeItem?.url && !activeItem?.video_url ? (
                    <video src={resolveUrl(activeItem.url)} className="w-full h-full object-cover" muted loop playsInline autoPlay />
                  ) : (
                    <RevealImage src={getOptimizedUrl(activeItem)} thumbSrc={getThumbUrl(activeItem)} alt={activeItem?.title} className="w-full h-full" eager />
                  )}
                </motion.div>
              </AnimatePresence>

              {/* Cinematic letterbox bars */}
              <div className="absolute top-0 left-0 right-0 h-12 bg-gradient-to-b from-black/50 to-transparent pointer-events-none" />
              <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-black/70 to-transparent pointer-events-none" />

              {/* Title overlay */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeIndex}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.4, delay: 0.2 }}
                  className="absolute bottom-5 left-6 md:left-8 z-10"
                >
                  <span className="font-heading text-xs md:text-sm tracking-[0.2em] uppercase text-white drop-shadow-lg">
                    {activeItem?.title}
                  </span>
                </motion.div>
              </AnimatePresence>

              {/* Counter */}
              <div className="absolute bottom-5 right-6 md:right-8 z-10 flex items-center gap-2">
                <span className="text-gold font-heading text-xs tracking-widest">
                  {String(activeIndex + 1).padStart(2, '0')}
                </span>
                <span className="text-zinc-600 text-[10px]">/</span>
                <span className="text-zinc-600 font-heading text-xs tracking-widest">
                  {String(items.length).padStart(2, '0')}
                </span>
              </div>

              {/* Expand icon */}
              <div className="absolute top-4 right-4 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <Expand className="w-5 h-5 text-white/60" />
              </div>

              {/* Nav arrows */}
              <button
                onClick={(e) => { e.stopPropagation(); go(-1); }}
                className="absolute left-3 md:left-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm border border-white/10 opacity-0 group-hover:opacity-100 hover:border-gold/40 hover:bg-black/60 transition-all duration-300"
                data-testid="gallery-prev"
              >
                <ChevronLeft className="w-5 h-5 text-white/80" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); go(1); }}
                className="absolute right-3 md:right-5 top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm border border-white/10 opacity-0 group-hover:opacity-100 hover:border-gold/40 hover:bg-black/60 transition-all duration-300"
                data-testid="gallery-next"
              >
                <ChevronRight className="w-5 h-5 text-white/80" />
              </button>

              {/* Gold border frame */}
              <div className="absolute inset-0 border border-gold/10 pointer-events-none" />
            </div>

            {/* Progress bar */}
            <div className="absolute -bottom-1 left-0 right-0 h-[2px] bg-white/5">
              <motion.div
                key={activeIndex}
                className="h-full bg-gold/60"
                initial={{ width: '0%' }}
                animate={{ width: '100%' }}
                transition={{ duration: isAutoPlay ? 4.5 : 60, ease: 'linear' }}
              />
            </div>
          </div>

          {/* Thumbnail strip */}
          <div className="flex gap-2 md:gap-3 overflow-x-auto pb-2 scrollbar-hide mt-5">
            {items.map((item, i) => (
              <motion.button
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.3 + i * 0.06 }}
                onClick={() => { setDirection(i > activeIndex ? 1 : -1); setActiveIndex(i); setIsAutoPlay(false); }}
                className={`relative flex-shrink-0 w-24 md:w-32 lg:w-40 aspect-[16/10] overflow-hidden transition-all duration-500 ${
                  i === activeIndex
                    ? 'ring-1 ring-gold/60 scale-[1.02]'
                    : 'opacity-40 hover:opacity-70 grayscale hover:grayscale-0'
                }`}
                data-testid={`gallery-thumb-${i}`}
              >
                {item.media_type === 'video' && item.url && !item.video_url ? (
                  <video src={`${resolveUrl(item.url)}#t=0.5`} className="w-full h-full object-cover" muted preload="metadata" />
                ) : (
                  <RevealImage src={getThumbUrl(item)} alt={item.title} className="w-full h-full" eager={Math.abs(i - activeIndex) <= 2} />
                )}
                {/* Active gold glow under thumbnail */}
                {i === activeIndex && (
                  <motion.div
                    layoutId="thumb-glow"
                    className="absolute -bottom-1 left-1/4 right-1/4 h-[2px] bg-gold rounded-full"
                    style={{ boxShadow: '0 0 8px rgba(191,155,48,0.5)' }}
                  />
                )}
              </motion.button>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Fullscreen lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-black/95 backdrop-blur-md flex items-center justify-center"
            onClick={() => setLightbox(false)}
            data-testid="lightbox"
          >
            <button
              className="absolute top-6 right-6 text-white/40 hover:text-gold transition-colors z-10"
              onClick={() => setLightbox(false)}
              data-testid="lightbox-close"
            >
              <X className="w-7 h-7" />
            </button>

            {/* Lightbox nav */}
            <button
              onClick={(e) => { e.stopPropagation(); go(-1); }}
              className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:border-gold/40 transition-all"
            >
              <ChevronLeft className="w-6 h-6 text-white/70" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); go(1); }}
              className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 z-10 w-12 h-12 flex items-center justify-center rounded-full bg-white/5 border border-white/10 hover:border-gold/40 transition-all"
            >
              <ChevronRight className="w-6 h-6 text-white/70" />
            </button>

            <AnimatePresence initial={false} custom={direction} mode="popLayout">
              <motion.div
                key={activeIndex}
                custom={direction}
                initial={{ opacity: 0, scale: 0.9, x: direction > 0 ? 60 : -60 }}
                animate={{ opacity: 1, scale: 1, x: 0 }}
                exit={{ opacity: 0, scale: 0.9, x: direction > 0 ? -60 : 60 }}
                transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
                className="max-w-[90vw] max-h-[85vh]"
                onClick={(e) => e.stopPropagation()}
              >
                {activeItem?.media_type === 'video' && activeItem?.url && !activeItem?.video_url ? (
                  <video src={resolveUrl(activeItem.url)} className="max-w-full max-h-[85vh] object-contain" controls autoPlay muted />
                ) : (
                  <img src={resolveUrl(activeItem?.url)} alt={activeItem?.title} className="max-w-full max-h-[85vh] object-contain" loading="eager" />
                )}
              </motion.div>
            </AnimatePresence>

            {/* Lightbox title + counter */}
            <div className="absolute bottom-8 left-0 right-0 flex justify-center items-center gap-6">
              <span className="font-heading text-sm tracking-[0.2em] uppercase text-gold/80">
                {activeItem?.title}
              </span>
              <span className="text-zinc-600 text-xs">
                {activeIndex + 1} / {items.length}
              </span>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
